import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Card } from "../../components/ui/Card";
import { Eyebrow } from "../../components/ui/Eyebrow";
import { Chip } from "../../components/ui/Chip";
import { CtaButton } from "../../components/ui/Button";
import { usePhotos } from "./usePhotos";
import { PhotoCompare } from "./PhotoCompare";
import { formatDate } from "../../lib/format";

export function PhotosPage() {
  const { photos, loading, uploadPhoto, signedUrl } = usePhotos();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [beforeId, setBeforeId] = useState<string>("");
  const [afterId, setAfterId] = useState<string>("");
  const [beforeUrl, setBeforeUrl] = useState<string | null>(null);
  const [afterUrl, setAfterUrl] = useState<string | null>(null);

  useEffect(() => {
    if (photos.length === 0) return;
    if (!beforeId) setBeforeId(photos[photos.length - 1].id);
    if (!afterId) setAfterId(photos[0].id);
  }, [photos, beforeId, afterId]);

  useEffect(() => {
    const before = photos.find((p) => p.id === beforeId);
    const after = photos.find((p) => p.id === afterId);
    if (before) signedUrl(before.storage_path).then(setBeforeUrl);
    if (after) signedUrl(after.storage_path).then(setAfterUrl);
  }, [beforeId, afterId, photos, signedUrl]);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadPhoto(file, "frontal");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  const before = photos.find((p) => p.id === beforeId);
  const after = photos.find((p) => p.id === afterId);

  return (
    <>
      <Card>
        <Eyebrow
          right={
            <Chip>
              <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Privado · PIN
            </Chip>
          }
        >
          Progreso visual
        </Eyebrow>

        {photos.length >= 2 && before && after && beforeUrl && afterUrl ? (
          <>
            <PhotoCompare
              beforeUrl={beforeUrl}
              afterUrl={afterUrl}
              beforeLabel={formatDate(before.date)}
              afterLabel={formatDate(after.date)}
            />
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <select
                value={beforeId}
                onChange={(e) => setBeforeId(e.target.value)}
                className="rounded-inner border border-line bg-bg1 px-2 py-2 font-mono text-[11px] text-text"
              >
                {photos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {formatDate(p.date)}
                  </option>
                ))}
              </select>
              <select
                value={afterId}
                onChange={(e) => setAfterId(e.target.value)}
                className="rounded-inner border border-line bg-bg1 px-2 py-2 font-mono text-[11px] text-text"
              >
                {photos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {formatDate(p.date)}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted">
            {loading ? "Cargando…" : "Sube al menos 2 fotos para activar el comparador."}
          </p>
        )}
        <p className="mt-2.5 text-[11.5px] text-muted">
          Mismo protocolo siempre: misma luz, misma distancia, relajado y en tensión.
        </p>
      </Card>

      <Card>
        <Eyebrow>Subir foto de hoy</Eyebrow>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFile}
          className="hidden"
        />
        <CtaButton onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? "Subiendo…" : "Añadir foto"}
        </CtaButton>
      </Card>
    </>
  );
}
